<script>
import RechargePackage from '@/model/dataLayer/api/MembershipSystem/RechargePackage'
import { showErrorMessage, showSuccessMessage, saveFile } from '@/model/utils/utils'
import { getRechargePackageFormSchema } from '@/model/baseForm/rechargePackageFormSchema'
import PageSubHeader from '@/views/widget/typography/PageSubHeader.vue'
import { useBaseFormDialogStore } from '@/model/baseForm/baseFormDialogStore'
import IKUtils from 'innerken-js-utils'
import GlobalConfig from '@/model/global/GlobalSettings'

export default {
  name: 'RechargePackage',
  components: {
    PageSubHeader
  },
  data () {
    return {
      packages: [],
      loading: false,
      submitting: false,
      blId: null, // This will be set based on your application's context
      model: RechargePackage
    }
  },
  created () {
    // Initialize component
    this.init()
  },
  methods: {
    // Load packages
    async loadPackages () {
      this.loading = true
      try {
        this.packages = await RechargePackage.getList(false, this.blId)
      } catch (error) {
        console.error('Error loading packages:', error)
        showErrorMessage('加载充值套餐失败: ' + (error.message || '未知错误'))
      } finally {
        this.loading = false
      }
    },

    // Open form to add a new package
    async openAddForm () {
      const store = useBaseFormDialogStore()

      // Prepare the form schema
      const formSchema = getRechargePackageFormSchema()

      // Set default values
      const defaultData = {
        active: true,
        bonusAmount: 0,
        sortOrder: 50
      }

      // Open the dialog
      const formData = await store.editItem({
        title: '添加充值套餐',
        subtitle: '创建新的充值套餐',
        schemas: formSchema
      }, defaultData)

      // Handle the result
      if (formData) {
        await this.handleFormSubmit(formData, null)
      }
    },

    // Open form to edit an existing package
    async openEditForm (pkg) {
      const store = useBaseFormDialogStore()

      // Deep copy the package to avoid modifying the original
      const editingPackage = IKUtils.deepCopy(pkg)

      // Prepare the form schema
      const formSchema = getRechargePackageFormSchema()

      // Open the dialog
      const formData = await store.editItem({
        title: '编辑充值套餐',
        subtitle: '修改充值套餐信息',
        schemas: formSchema
      }, editingPackage)

      // Handle the result
      if (formData) {
        await this.handleFormSubmit(formData, editingPackage)
      }
    },

    // Handle form submission (add or edit)
    async handleFormSubmit (formData, editingPackage) {
      this.submitting = true
      try {
        // Ensure blId is set from GlobalConfig
        formData.blId = GlobalConfig.blId

        // Handle image upload - use uploadFile API
        if (formData.imageUrl instanceof File || formData.imageUrl instanceof Blob) {
          formData.imageUrl = await saveFile(formData.imageUrl)
        }

        if (editingPackage) {
          // Edit existing package
          formData.id = editingPackage.id
          await RechargePackage.edit(formData)
          showSuccessMessage('充值套餐更新成功')
        } else {
          // Add new package
          await RechargePackage.add(formData)
          showSuccessMessage('充值套餐添加成功')
        }

        await this.loadPackages()
      } catch (error) {
        console.error('Error saving recharge package:', error)
        showErrorMessage('保存失败: ' + (error.message || '未知错误'))
      } finally {
        this.submitting = false
      }
    },

    // Delete a package
    async deletePackage (pkg) {
      if (!confirm(`确定要删除充值套餐 "${pkg.name}" 吗？`)) {
        return
      }

      try {
        await RechargePackage.delete(pkg.id)
        showSuccessMessage('充值套餐删除成功')
        await this.loadPackages()
      } catch (error) {
        console.error('Error deleting recharge package:', error)
        showErrorMessage('删除失败: ' + (error.message || '未知错误'))
      }
    },

    // Calculate total amount (charge + bonus)
    calculateTotalAmount (pkg) {
      const charge = parseFloat(pkg.chargeAmount) || 0
      const bonus = parseFloat(pkg.bonusAmount) || 0
      return (charge + bonus).toFixed(2)
    },

    // Format date for display
    formatDate (dateString) {
      if (!dateString) return '无限制'
      const date = new Date(dateString)
      return date.toLocaleString()
    },

    // Initialize component
    async init () {
      // Set business layer ID from GlobalConfig
      this.blId = GlobalConfig.blId
      await this.loadPackages()
    }
  }
}
</script>

<template>
  <v-container>
    <div
      v-if="loading"
      class="text-center my-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      />
      <div class="mt-4 text-body-1 grey--text text--darken-1">
        加载中...
      </div>
    </div>

    <div v-else>
      <page-sub-header class="mb-4">
        充值套餐管理
        <template #subtitle>
          管理会员充值套餐，包括创建、编辑和删除充值套餐
        </template>
        <template #action>
          <v-btn
            color="primary"
            elevation="2"
            class="px-4"
            @click="openAddForm"
          >
            <v-icon left>
              mdi-plus
            </v-icon>
            添加套餐
          </v-btn>
        </template>
      </page-sub-header>

      <div
        v-if="packages.length === 0"
        class="text-center pa-8 empty-state"
      >
        <v-icon
          size="80"
          color="grey lighten-1"
        >
          mdi-cash-multiple
        </v-icon>
        <div class="text-h5 grey--text text--darken-1 mt-4">
          暂无充值套餐
        </div>
        <div class="text-body-1 grey--text mt-2">
          点击添加按钮创建第一个充值套餐
        </div>
        <v-btn
          color="primary"
          class="mt-6"
          @click="openAddForm"
        >
          <v-icon left>
            mdi-plus
          </v-icon>
          添加套餐
        </v-btn>
      </div>

      <div v-else>
        <v-row>
          <v-col
            v-for="pkg in packages"
            :key="pkg.id"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <v-card
              class="package-card"
              :class="{'inactive-package': !pkg.active}"
              elevation="2"
              hover
              shaped
            >
              <!-- Status & Featured Badges -->
              <div class="package-badges">
                <v-chip
                  v-if="!pkg.active"
                  class="package-status"
                  color="error"
                  small
                  label
                >
                  <v-icon
                    left
                    size="small"
                  >
                    mdi-close-circle
                  </v-icon>
                  未激活
                </v-chip>

              </div>

              <!-- Package Header -->
              <v-card-title class="package-header">
                <v-icon
                  v-if="!pkg.imageUrl"
                  left
                  color="primary"
                >
                  mdi-cash-multiple
                </v-icon>
                <span>{{ pkg.name }}</span>
              </v-card-title>

              <!-- Package Content -->
              <v-card-text class="package-content">
                <!-- Price Display (Simplified) -->
                <div class="price-display-simple">
                  <div class="price-info">
                    <span class="price-label">充值金额:</span>
                    <span class="price-value">{{ pkg.chargeAmount | priceDisplay }}</span>
                  </div>

                  <div v-if="pkg.bonusAmount > 0" class="bonus-info">
                    <span class="price-label">赠送金额:</span>
                    <span class="price-value">{{ pkg.bonusAmount | priceDisplay }}</span>
                  </div>

                  <div v-if="pkg.bonusAmount > 0" class="total-info">
                    <span class="price-label">总价值:</span>
                    <span class="price-value">{{ calculateTotalAmount(pkg) | priceDisplay }}</span>
                  </div>
                </div>

                <!-- Package Details (Simplified) -->
                <div class="package-details-simple">
                  <div v-if="pkg.description" class="detail-item">
                    <v-icon small class="mr-1">mdi-information-outline</v-icon>
                    <span class="detail-text">{{ pkg.description }}</span>
                  </div>

                  <div v-if="pkg.requiredLevelOrder !== null && pkg.requiredLevelOrder !== undefined" class="detail-item">
                    <v-icon small class="mr-1">mdi-account-star</v-icon>
                    <span class="detail-text">会员等级要求: {{ pkg.requiredLevelOrder }}</span>
                  </div>

                  <div v-if="pkg.startDate || pkg.endDate" class="detail-item">
                    <v-icon small class="mr-1">mdi-calendar-range</v-icon>
                    <span class="detail-text">有效期: {{ pkg.startDate ? formatDate(pkg.startDate) : '无限制' }}
                      至 {{ pkg.endDate ? formatDate(pkg.endDate) : '无限制' }}</span>
                  </div>
                </div>
              </v-card-text>

              <!-- Actions (Simplified) -->
              <v-divider />
              <v-card-actions class="package-actions">
                <v-btn
                  small
                  text
                  color="primary"
                  @click="openEditForm(pkg)"
                >
                  <v-icon small left>mdi-pencil</v-icon>
                  编辑
                </v-btn>
                <v-btn
                  small
                  text
                  color="error"
                  @click="deletePackage(pkg)"
                >
                  <v-icon small left>mdi-delete</v-icon>
                  删除
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </div>
  </v-container>
</template>

<style scoped>
.empty-state {
  background-color: #f9f9f9;
  border-radius: 12px;
  margin-top: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.package-card {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: white;
  transition: box-shadow 0.2s ease;
}

.package-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
}

.inactive-package {
  opacity: 0.8;
  background-color: #f5f5f5;
}

.package-badges {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.package-header {
  padding: 12px 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.package-content {
  flex: 1;
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.price-display-simple {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #f9f9f9;
}

.price-info, .bonus-info, .total-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.price-label {
  color: #757575;
  font-size: 0.875rem;
}

.price-value {
  font-weight: 500;
  color: #1976d2;
}

.total-info {
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px dashed #e0e0e0;
}

.total-info .price-value {
  font-weight: 700;
  color: #d32f2f;
}

.package-details-simple {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-item {
  display: flex;
  align-items: flex-start;
  font-size: 0.875rem;
  color: #616161;
}

.detail-text {
  flex: 1;
  line-height: 1.4;
}

.package-actions {
  padding: 4px 8px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
